import React from "react";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Message } from "primereact/message";

import Loader from "../../components/Loader";
import useContractAnalytics from "../../../hooks/useContractAnalytics";
import {
  SIGN_METHOD_OPTIONS,
  signMethodLabel,
} from "../../../constants/contractSigning";
import { SignMethod } from "../../../types/onboarding";

const METHOD_FILTER_OPTIONS = [
  { value: null, label: "All methods" },
  ...SIGN_METHOD_OPTIONS,
];

// Format hours as a compact "Xh" / "Xd Yh" duration; "—" when unknown.
// Round the total first so the remainder can never render as "24 h".
const formatDuration = (hours?: number | null): string => {
  if (hours === null || hours === undefined) return "—";
  const total = Math.round(hours);
  if (total < 24) return `${total} h`;
  const days = Math.floor(total / 24);
  const rem = total % 24;
  return rem ? `${days} d ${rem} h` : `${days} d`;
};

// Serialize a picked Date as a local YYYY-MM-DD (toISOString would shift the day
// under non-UTC timezones).
const toIsoDate = (d: Date | null | undefined): string | null => {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="col-12 md:col-4">
    <div className="p-3 border-1 border-round surface-border h-full">
      <div className="text-500 text-sm">{label}</div>
      <div className="text-2xl font-medium mt-1">{value}</div>
    </div>
  </div>
);

/**
 * US5: aggregate contract-signing analytics — totals, average and typical
 * time-to-sign, and a breakdown by signature method, filterable by method and
 * date range.
 */
const ContractAnalyticsPage: React.FC = () => {
  const { data, loading, error, filters, setFilters, isEmpty } =
    useContractAnalytics();

  const fromDate = filters.from ? new Date(filters.from) : null;
  const toDate = filters.to ? new Date(filters.to) : null;

  const byMethod = data?.by_sign_method ?? {};

  return (
    <div className="card onboarding-page">
      <h2 className="mt-0">Contract analytics</h2>

      <div className="flex flex-wrap gap-3 align-items-end mb-4">
        <div className="flex flex-column">
          <label htmlFor="method_filter" className="text-500 text-sm mb-1">
            Signature method
          </label>
          <Dropdown
            inputId="method_filter"
            ariaLabel="Signature method"
            value={filters.signMethod ?? null}
            options={METHOD_FILTER_OPTIONS}
            optionLabel="label"
            optionValue="value"
            onChange={(e) =>
              setFilters({ ...filters, signMethod: e.value ?? null })
            }
            style={{ minWidth: "14rem" }}
          />
        </div>
        <div className="flex flex-column">
          <label htmlFor="from_filter" className="text-500 text-sm mb-1">
            From
          </label>
          <Calendar
            inputId="from_filter"
            value={fromDate}
            dateFormat="yy-mm-dd"
            showButtonBar
            onChange={(e) =>
              setFilters({ ...filters, from: toIsoDate(e.value as Date) })
            }
          />
        </div>
        <div className="flex flex-column">
          <label htmlFor="to_filter" className="text-500 text-sm mb-1">
            To
          </label>
          <Calendar
            inputId="to_filter"
            value={toDate}
            dateFormat="yy-mm-dd"
            showButtonBar
            onChange={(e) =>
              setFilters({ ...filters, to: toIsoDate(e.value as Date) })
            }
          />
        </div>
      </div>

      {loading && <Loader />}

      {!loading && error && (
        <Message
          severity="error"
          className="w-full"
          text="Contract analytics could not be loaded."
        />
      )}

      {!loading && !error && isEmpty && (
        <Message
          severity="info"
          className="w-full"
          text="No contracts match the current filters."
        />
      )}

      {!loading && !error && !isEmpty && data && (
        <>
          <div className="grid mb-4">
            <Stat label="Total contracts" value={data.total_contracts} />
            <Stat
              label="Average time to sign"
              value={formatDuration(data.avg_time_to_sign_hours)}
            />
            <Stat
              label="Typical (median) time to sign"
              value={formatDuration(data.median_time_to_sign_hours)}
            />
          </div>

          <h3>By signature method</h3>
          <div className="grid">
            {(Object.keys(byMethod) as SignMethod[]).map((m) => (
              <div className="col-12 md:col-4" key={m}>
                <div className="p-3 border-1 border-round surface-border">
                  <div className="font-medium">{signMethodLabel(m)}</div>
                  <div className="text-500 text-sm mt-1">
                    {byMethod[m]?.count ?? 0} contracts ·{" "}
                    {formatDuration(byMethod[m]?.avg_hours)} avg
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ContractAnalyticsPage;
